import type {
  CashGame,
  DayMeta,
  SitGo,
  Tournament,
  TournamentStatus
} from 'components/SaloonLobby/types';
import { SEATS_BY_FORMAT } from 'components/SaloonLobby/useLobbyFilters';

/**
 * DEMO TABLES (Lobby Lab). A lower environment's cash lobby is not the room
 * players see: it runs one table at a stake, and its stake ladder is whatever
 * the environment happens to have been seeded with — rungs production does not
 * offer ($0.01/$0.02, $4/$8, $20/$40, $300/$600) alongside gaps where
 * production has a rung ($0.05/$0.10, $25/$50). With the switch on, the lobby
 * is projected onto PRODUCTION_CASH_STAKES: the ladder below, read off the live
 * lobby (owner, 2026-09-09).
 *
 * For each rung, in production's own order:
 *   • live tables at that rung keep their place and their data, and still open
 *     for real;
 *   • the rung is padded UP TO `minPerStake` (nine) with demo tables — variants
 *     rotating NLH → PLO → PLO Hi-Lo, 9- and 6-max capacities, a spread of seat
 *     counts including one full table so the Join Waitlist banner shows once;
 *   • a rung with no live table at all is built from the ladder itself;
 *   • and every tile on the rung, live or demo, advertises production's buy-in
 *     spread — 20× to 100× the big blind.
 * Live tables at a stake production does not offer are left out, which is the
 * point: the demo room is production's room.
 *
 * The buy-in normalisation is the one place this fixture overwrites live data
 * (owner, 2026-09-09). A lower environment seeds its minimum at 10× the big
 * blind, so without it eight of the eleven rungs advertised half of what
 * production asks for. The cost is that a live tile in a demo shows a buy-in
 * its table will not actually offer when opened; demo tiles cannot be opened at
 * all, so they cannot disagree with anything.
 *
 * Deterministic per stake, so a re-render never reshuffles the room. Demo ids
 * carry DEMO_TABLE_PREFIX and that string prefix is the only thing that gates
 * opening them; their table NUMBER is a plausible display value so the demo
 * room looks like production, which prints a number on every card.
 */
export const DEMO_TABLE_PREFIX = 'demo-';
export const DEMO_MIN_PER_STAKE = 9;

/**
 * The cash stakes production offers, in production's order (owner, 2026-09-09,
 * from app.hijackpoker.com). Blinds only — everything else about a rung is
 * derived: the label the way the lobby writes it (`stakesLabel.ts`) and the
 * buy-in spread the engine sets at 20×/100× the big blind.
 */
export const PRODUCTION_CASH_STAKES: readonly { readonly sb: number; readonly bb: number }[] = [
  { sb: 0.02, bb: 0.05 },
  { sb: 0.05, bb: 0.1 },
  { sb: 0.1, bb: 0.2 },
  { sb: 0.25, bb: 0.5 },
  { sb: 0.5, bb: 1 },
  { sb: 1, bb: 2 },
  { sb: 2, bb: 4 },
  { sb: 3, bb: 6 },
  { sb: 5, bb: 10 },
  { sb: 10, bb: 20 },
  { sb: 25, bb: 50 }
];

const money = (amount: number) => amount.toFixed(2);

/** Cents, so a rung whose big blind is not a round number cannot drift. */
const cents = (amount: number) => Math.round(amount * 100) / 100;

/** What production asks for at a rung: 20× to 100× the big blind. */
const productionBuyIn = (bb: number) => ({
  buyInMin: cents(20 * bb),
  buyInMax: cents(100 * bb)
});

/** A rung's stakes exactly as the lobby writes them: "$0.02 / $0.05". */
export const demoStakesLabel = (sb: number, bb: number) => `$${money(sb)} / $${money(bb)}`;

/** The blinds a stakes string carries, however it is spaced; null if it has none. */
export const parseStakes = (label: string): { sb: number; bb: number } | null => {
  const parts = label.split('/');
  if (parts.length !== 2) return null;
  const [sb, bb] = parts.map((part) => parseFloat(part.replace(/[^0-9.]/g, '')));
  if (!Number.isFinite(sb) || !Number.isFinite(bb)) return null;
  return { sb, bb };
};

/** One key per rung, so "$1/$2" and "$1.00 / $2.00" land on the same one. */
const rungKey = (sb: number, bb: number) => `${money(sb)}/${money(bb)}`;

const VARIANTS: CashGame['variant'][] = ['NLH', 'PLO', 'PLO-HiLo'];
const BANNER: Record<string, string> = {
  NLH: 'cash-nlh-v1.png',
  PLO: 'cash-plo-v1.png',
  'PLO-HiLo': 'cash-plo-hilo-v1.png'
};
// How full each demo slot is, as a share of its capacity: a busy table, ONE
// full one (so the waitlist banner shows exactly once per padded stake), then a
// spread of open seats. Indexed by the slot's position in the group, so a group
// that already has two live tables simply skips the first entries — and slot 0
// is only ever used by a rung production offers that this environment does not
// run at all.
const FILL_BY_SLOT: Record<number, number> = {
  0: 0.45,
  1: 0.55,
  2: 1,
  3: 0.3,
  4: 0.8,
  5: 0.15,
  6: 0.65,
  7: 0.4,
  8: 0.9
};

export const isDemoTableId = (id: string): boolean => id.startsWith(DEMO_TABLE_PREFIX);

const slug = (s: string) =>
  s
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();

export const padCashGames = (games: CashGame[], minPerStake = DEMO_MIN_PER_STAKE): CashGame[] => {
  // Live tables, bucketed onto the rung they belong to. A table at a stake
  // production does not offer has no rung and is left out of the demo room.
  const liveByRung = new Map<string, CashGame[]>();
  games.forEach((game) => {
    const blinds = parseStakes(game.stakes);
    if (!blinds) return;
    const key = rungKey(blinds.sb, blinds.bb);
    const list = liveByRung.get(key) ?? [];
    list.push(game);
    liveByRung.set(key, list);
  });

  const out: CashGame[] = [];
  let serial = 0;
  PRODUCTION_CASH_STAKES.forEach(({ sb, bb }) => {
    const live = liveByRung.get(rungKey(sb, bb)) ?? [];
    const buyIn = productionBuyIn(bb);
    // A live table's own label wins, so a rung the environment runs keeps the
    // exact string its tiles and its heading already share.
    const stakes = live[0]?.stakes ?? demoStakesLabel(sb, bb);
    const base: CashGame =
      live[0] ??
      ({
        id: '',
        tableId: 0,
        variant: 'NLH',
        banner: BANNER.NLH,
        stakes,
        sb,
        ...buyIn,
        seated: 0,
        capacity: 9,
        game: 'texas'
      } as CashGame);
    const startVariant = Math.max(0, VARIANTS.indexOf(base.variant));

    // A live table keeps its identity, its seats and its variant; only the
    // buy-in it advertises is replaced, so the whole rung reads as production's.
    out.push(...live.map((game) => ({ ...game, ...buyIn })));
    for (let i = live.length; i < minPerStake; i++) {
      const variant = VARIANTS[(startVariant + i) % VARIANTS.length];
      const capacity = i % 2 === 0 ? 9 : 6;
      const seated = Math.min(capacity, Math.round((FILL_BY_SLOT[i] ?? 0.5) * capacity));
      serial += 1;
      out.push({
        ...base,
        ...buyIn,
        id: `${DEMO_TABLE_PREFIX}${slug(stakes)}-${i}`,
        // A PLAUSIBLE table number, in production's six-digit range (owner,
        // 2026-09-10). These used to be negative so they could never collide
        // with an engine id, but the card only prints a number when it is
        // positive — so a demo room showed a number on the handful of live
        // tables and nothing on the ~90 padded ones, which is not what
        // production looks like.
        //
        // Safe, because the number was never the guard: `isDemoTableId` reads
        // the `demo-` prefix on the string `id`, and that is what the container
        // checks before opening a table. The number is display only.
        //
        // Deterministic and unique per rung: the serial keeps them distinct and
        // 900000 keeps them clear of the ids the engine is issuing today.
        tableId: 900000 + serial,
        stakes,
        sb,
        variant,
        banner: BANNER[variant] ?? base.banner,
        game: variant === 'NLH' ? 'texas' : 'omaha',
        capacity,
        seated,
        waitlistCount: 0
      });
    }
  });
  return out;
};

/**
 * HIGH TRAFFIC (Lobby Lab). What the lobby looks like on a busy night: in every
 * stake group, every other table is full and carries a short waitlist, so half
 * the tiles wear the Join Waitlist banner; the other half keep their seats but
 * never show as full, so "half" stays exactly half. Applies to live and demo
 * tables alike — it is a look, not data; live tables still open for real.
 */
export const applyHighTraffic = (games: CashGame[]): CashGame[] => {
  const seen = new Map<string, number>();
  return games.map((g) => {
    const i = seen.get(g.stakes) ?? 0;
    seen.set(g.stakes, i + 1);
    if (i % 2 === 0) {
      return { ...g, seated: g.capacity, waitlistCount: 1 + (i % 3) };
    }
    return g.seated >= g.capacity ? { ...g, seated: g.capacity - 1 } : g;
  });
};

/**
 * SIT & GO DEMO (Lobby Lab, owner 2026-09-10). The same idea as the cash room
 * above, for the Sit & Go tab: a lower environment's sit & gos are a few
 * hand-seeded dev rows ("de 2", "SNG 3P", "sng tgo") with prize pools set to
 * roughly half of buy-in × seats, so the tab reads $4 / $6 / $10 / $15 — small
 * enough to be mistaken for buy-ins. Production's ladder is the one below.
 *
 * The heading under which a sit & go is grouped is its PRIZE POOL and the tile
 * shows its BUY-IN; that is the shipped design and this fixture does not change
 * it. What it changes is the data: with the switch on, the tab is projected onto
 * PRODUCTION_SITGO_LADDER — live sit & gos whose buy-in and prize pool match a
 * rung keep their place and still open for real; a rung with none is built from
 * the ladder; sit & gos at a rung production does not offer are left out.
 */
export const PRODUCTION_SITGO_LADDER: readonly {
  readonly buyIn: number;
  readonly prizePool: number;
  readonly capacity: number;
}[] = [
  { buyIn: 6, prizePool: 30, capacity: 6 },
  { buyIn: 22, prizePool: 120, capacity: 6 },
  { buyIn: 110, prizePool: 600, capacity: 6 },
  { buyIn: 550, prizePool: 3000, capacity: 6 },
  { buyIn: 1100, prizePool: 6000, capacity: 6 }
];

/**
 * HEADS_UP_SITGO_LADDER — the heads-up product leadership asked for (Ryan,
 * 2026-09 exec meeting), priced off the six-seat ladder so the two are ONE
 * model. Production's rung is really "stake + fee": $22 is $20 + $2, and the
 * pool is six seats of the $20 — 120. So a heads-up pool is TWO seats of the
 * same per-seat stake at the same buy-in: $6 → $10, $22 → $40, $110 → $200,
 * $550 → $1,000, $1,100 → $2,000. Whole numbers a player can check in their
 * head, and the house keeps the fee it already charges — about 9% of the buy-in
 * (17% on the cheapest rung, exactly as today). Nothing is invented: change the
 * six-seat ladder and this one follows.
 *
 * Projected only while the Sit & Go seat toggle is on (EN-10747), so the demo
 * never shows a format the tab has no way to reach. Retire it once real
 * two-seat tables exist (EN-10745), or keep it for demos.
 */
export const HEADS_UP_SEATS = SEATS_BY_FORMAT.headsUp;
export const HEADS_UP_SITGO_LADDER: readonly {
  readonly buyIn: number;
  readonly prizePool: number;
  readonly capacity: number;
}[] = PRODUCTION_SITGO_LADDER.map(({ buyIn, prizePool, capacity }) => ({
  buyIn,
  prizePool: (prizePool / capacity) * HEADS_UP_SEATS,
  capacity: HEADS_UP_SEATS
}));

/** What production names these — the tile's small top line. */
export const DEMO_SITGO_NAME = 'Sit-n-Go';
/** The heads-up tables' name; the card carries no other heads-up marker yet (EN-10719's). */
export const DEMO_HEADS_UP_NAME = 'Heads-Up Sit-n-Go';

/** How many tables the cheapest pool of each ladder carries in the demo (owner's wrap test). */
export type CheapestPoolTables = 1 | 2 | 3;

export interface ProjectSitGosOptions {
  /** Also project the heads-up ladder (only while the tab's seat toggle can reach it). */
  headsUp?: boolean;
  /**
   * Tables in the CHEAPEST pool of each ladder: the open one first, then full
   * ones — a pool that filled and spawned its next table. Lets the owner watch
   * the other columns wrap under a taller one (round 2, 2026-09-10).
   */
  cheapestTables?: CheapestPoolTables;
}

const sitgoKey = (buyIn: number, prizePool: number) => `${money(buyIn)}/${money(prizePool)}`;

export const projectSitGos = (live: SitGo[], opts: ProjectSitGosOptions = {}): SitGo[] => {
  const { headsUp = false, cheapestTables = 1 } = opts;
  const liveByRung = new Map<string, SitGo[]>();
  live.forEach((s) => {
    const key = sitgoKey(s.buyIn, s.prizePool);
    const list = liveByRung.get(key) ?? [];
    list.push(s);
    liveByRung.set(key, list);
  });

  type Rung = { buyIn: number; prizePool: number; capacity: number };
  const build = (
    rung: Rung,
    id: string,
    tableId: number,
    name: string,
    registered: number
  ): SitGo => ({
    id: `${DEMO_TABLE_PREFIX}${id}`,
    // A plausible six-digit tournament number, like the cash demo tables;
    // the `demo-` prefix on the string id is what gates opening it.
    tableId,
    variant: 'NLH',
    banner: 'sitgo-nlh-v1.png',
    label: "NLH HOLD'EM",
    name,
    buyIn: rung.buyIn,
    prizePool: rung.prizePool,
    registered,
    capacity: rung.capacity,
    game: 'texas',
    isRegistered: false
  });

  const out: SitGo[] = [];
  const project = (ladder: readonly Rung[], name: string, idPrefix: string, tableBase: number) =>
    ladder.forEach((rung, i) => {
      const matches = liveByRung.get(sitgoKey(rung.buyIn, rung.prizePool)) ?? [];
      const pool = slug(String(rung.prizePool));
      // Production's tab showed every table waiting to fill.
      const tables =
        matches.length > 0
          ? [...matches]
          : [build(rung, `${idPrefix}-${pool}`, tableBase + i, name, 0)];
      // The cheapest pool is the one that fills: pad it with FULL tables, so the
      // open one leads and the filled ones trail (owner's ordering).
      if (i === 0) {
        for (let k = tables.length; k < cheapestTables; k += 1) {
          tables.push(
            build(
              rung,
              `${idPrefix}-${pool}-full-${k}`,
              tableBase + 100 * k + i,
              name,
              rung.capacity
            )
          );
        }
      }
      out.push(...tables);
    });

  project(PRODUCTION_SITGO_LADDER, DEMO_SITGO_NAME, 'sng', 910000);
  if (headsUp) project(HEADS_UP_SITGO_LADDER, DEMO_HEADS_UP_NAME, 'hu', 920000);
  return out;
};

// ---------------------------------------------------------------------------
// DEMO TOURNAMENTS — production's calendar, on this environment's clock.
//
// A lower environment runs a handful of hand-made tournaments on one or two
// days, which is not a schedule anyone can judge a design against. Production's
// tab (read off app.hijackpoker.com, owner's screenshot 2026-09-10) is a WEEK of
// full days followed by a scatter of announced specials:
//
//     today +0 … +6   the daily card, 5–6 events a day (11 on Friday)
//     +7, +8          one announced event each
//     +9, +16         two each
//     +20             one
//
// Twelve days carrying events, which is exactly what the owner's day row shows.
// The card is keyed on the WEEKDAY, not on a date, so the shape survives any
// "today": the counts land on different chips, the rhythm is production's.
//
// What is faithful vs inferred: every event on Sat–Wed is transcribed from that
// screenshot — title, time, buy-in, prize pool, type pill and status. Thursday's
// six and Friday's eleven were off the left edge of it, so their contents are
// built from the same weekly furniture (the 3pm freeroll, the 5pm bounty, the
// 7pm feature, the 9pm nightcap, the 10pm turbo freezeout) to hit the counts the
// day chips DO show. The far-out specials are likewise shaped, not transcribed.
//
// Nothing here can be opened: the ids carry the `demo-` prefix, which is the
// same guard the demo cash tables and sit & gos use.
// ---------------------------------------------------------------------------

/** One event in the weekly card. `at` is local wall-clock, as production quotes it. */
interface MttSlot {
  readonly at: readonly [hour: number, minute: number];
  readonly title: string;
  /** 0 renders as FREE on the tile. */
  readonly buyIn: number;
  readonly prizePool: number;
  readonly type: Tournament['type'];
  /** The pill on the tile. A re-entry freeroll reads "Re-entry", like production. */
  readonly typeLabel: string;
  readonly status?: TournamentStatus;
  readonly registered?: number;
  /** Satellites only: seats guaranteed, which the tile shows instead of a pool. */
  readonly seats?: number;
}

const FREEROLL: Pick<MttSlot, 'buyIn' | 'type' | 'typeLabel'> = {
  buyIn: 0,
  type: 'Freeroll',
  typeLabel: 'Re-entry'
};
const REENTRY: Pick<MttSlot, 'type' | 'typeLabel'> = { type: 'Re-Entry', typeLabel: 'Re-entry' };
const BOUNTY: Pick<MttSlot, 'type' | 'typeLabel'> = { type: 'Bounty', typeLabel: 'Bounty' };
const TURBO_FREEZEOUT: Omit<MttSlot, 'at' | 'status'> = {
  title: 'Turbo 10 Freezeout',
  buyIn: 22,
  prizePool: 1000,
  type: 'Freezeout',
  typeLabel: 'Freezeout'
};
const WSOPC_SATELLITE: Omit<MttSlot, 'at' | 'status'> = {
  title: 'WSOPC Main Event Satellite',
  buyIn: 200,
  prizePool: 0,
  type: 'Satellite',
  typeLabel: 'Re-entry',
  seats: 2
};
const DAILY_FREEROLL = (day: string): Omit<MttSlot, 'at' | 'status'> => ({
  ...FREEROLL,
  title: `${day} $1,000 Freeroll`,
  prizePool: 1000
});

/** Production's week, Sunday first — the array index IS `Date.getDay()`. */
const PRODUCTION_MTT_WEEK: readonly (readonly MttSlot[])[] = [
  // Sunday — 5
  [
    { at: [15, 0], ...FREEROLL, title: 'The MEGA MoneyTrain Freeroll', prizePool: 5000 },
    { at: [17, 0], ...REENTRY, title: 'Sunday High 5', buyIn: 6, prizePool: 10000 },
    {
      at: [19, 0],
      ...REENTRY,
      title: 'NEW Sunday Giant',
      buyIn: 220,
      prizePool: 31000,
      status: 'R'
    },
    {
      at: [21, 0],
      ...REENTRY,
      title: 'Week Ends Nightcap',
      buyIn: 33,
      prizePool: 2000,
      status: 'R'
    },
    { at: [22, 1], ...TURBO_FREEZEOUT }
  ],
  // Monday — 5
  [
    { at: [15, 0], ...DAILY_FREEROLL('Monday') },
    { at: [17, 0], ...BOUNTY, title: '10 Buck Bounty', buyIn: 33, prizePool: 1500, status: 'R' },
    {
      at: [19, 0],
      ...REENTRY,
      title: 'Monday High Five',
      buyIn: 6,
      prizePool: 10000,
      status: 'R',
      registered: 5
    },
    {
      at: [21, 0],
      ...REENTRY,
      title: '$3k 6 Max Crusher',
      buyIn: 55,
      prizePool: 3000,
      status: 'R'
    },
    { at: [22, 0], ...TURBO_FREEZEOUT, status: 'R' }
  ],
  // Tuesday — 6
  [
    { at: [13, 0], ...WSOPC_SATELLITE },
    { at: [15, 0], ...DAILY_FREEROLL('Tuesday') },
    { at: [17, 1], ...REENTRY, title: '2 2 Tuesday', buyIn: 22, prizePool: 1000, status: 'R' },
    { at: [19, 0], ...BOUNTY, title: 'NEW Big Bounty Special', buyIn: 220, prizePool: 20000 },
    {
      at: [21, 0],
      ...REENTRY,
      title: '$1k Tuesday Nightcap',
      buyIn: 22,
      prizePool: 1000,
      status: 'R'
    },
    { at: [22, 0], ...TURBO_FREEZEOUT, status: 'R' }
  ],
  // Wednesday — 5
  [
    { at: [15, 0], ...DAILY_FREEROLL('Wednesday') },
    { at: [17, 0], ...BOUNTY, title: '10 Buck Bounties', buyIn: 33, prizePool: 1500, status: 'R' },
    { at: [19, 2], ...REENTRY, title: 'Wednesday $10k Everyday', buyIn: 165, prizePool: 10000 },
    {
      at: [21, 0],
      ...REENTRY,
      title: '$3k 6 Max Crusher',
      buyIn: 55,
      prizePool: 3000,
      status: 'R'
    },
    { at: [22, 0], ...TURBO_FREEZEOUT }
  ],
  // Thursday — 6 (count from the day chips; contents from the weekly furniture)
  [
    { at: [15, 0], ...DAILY_FREEROLL('Thursday') },
    { at: [17, 0], ...BOUNTY, title: '10 Buck Bounty', buyIn: 33, prizePool: 1500, status: 'R' },
    { at: [19, 0], ...REENTRY, title: '$10k Everyday', buyIn: 165, prizePool: 10000 },
    {
      at: [21, 0],
      ...REENTRY,
      title: '$3k 6 Max Crusher',
      buyIn: 55,
      prizePool: 3000,
      status: 'R'
    },
    { at: [22, 0], ...TURBO_FREEZEOUT, status: 'R' },
    { at: [23, 0], ...REENTRY, title: 'Midnight Madness Turbo', buyIn: 22, prizePool: 2000 }
  ],
  // Friday — 11, the busiest day of production's week
  [
    { at: [8, 0], ...FREEROLL, title: '$500 Morning Freeroll', prizePool: 500 },
    { at: [12, 0], ...REENTRY, title: 'High Noon', buyIn: 33, prizePool: 2000, status: 'R' },
    { at: [13, 0], ...WSOPC_SATELLITE },
    { at: [15, 0], ...DAILY_FREEROLL('Friday') },
    { at: [17, 0], ...BOUNTY, title: '10 Buck Bounty', buyIn: 33, prizePool: 1500, status: 'R' },
    { at: [18, 0], ...REENTRY, title: 'Friday Night Fights', buyIn: 55, prizePool: 5000 },
    { at: [19, 0], ...REENTRY, title: '$10k Everyday', buyIn: 165, prizePool: 10000 },
    { at: [20, 0], ...BOUNTY, title: 'Big Bounty Special', buyIn: 220, prizePool: 20000 },
    { at: [21, 0], ...REENTRY, title: 'Friday Nightcap', buyIn: 33, prizePool: 2000, status: 'R' },
    { at: [22, 0], ...TURBO_FREEZEOUT, status: 'R' },
    { at: [23, 0], ...REENTRY, title: 'Midnight Madness Turbo', buyIn: 22, prizePool: 2000 }
  ],
  // Saturday — 5
  [
    { at: [15, 0], ...DAILY_FREEROLL('Saturday') },
    { at: [17, 0], ...REENTRY, title: 'Game Day $10k', buyIn: 33, prizePool: 10000, status: 'R' },
    { at: [19, 1], ...REENTRY, title: '$10k Everyday', buyIn: 165, prizePool: 10000, status: 'R' },
    {
      at: [21, 0],
      ...REENTRY,
      title: 'Saturday Nightcap',
      buyIn: 33,
      prizePool: 2000,
      status: 'R'
    },
    { at: [22, 1], ...TURBO_FREEZEOUT, status: 'R' }
  ]
];

/** How far ahead the full daily card is published. */
export const MTT_CARD_DAYS = 7;

/**
 * Announced specials past the card, keyed by how many days out they are — the
 * scatter production's day row shows once the daily card runs out.
 */
const PRODUCTION_MTT_SPECIALS: readonly (readonly [offset: number, slots: readonly MttSlot[]])[] = [
  [7, [{ at: [15, 0], ...DAILY_FREEROLL('Daily') }]],
  [8, [{ at: [15, 0], ...DAILY_FREEROLL('Daily') }]],
  [
    9,
    [
      { at: [13, 0], ...WSOPC_SATELLITE },
      { at: [15, 0], ...DAILY_FREEROLL('Daily') }
    ]
  ],
  [
    16,
    [
      { at: [13, 0], ...WSOPC_SATELLITE },
      { at: [19, 0], ...BOUNTY, title: 'Big Bounty Special', buyIn: 220, prizePool: 20000 }
    ]
  ],
  [
    20,
    [{ at: [17, 0], ...REENTRY, title: 'Monthly $50K Guaranteed', buyIn: 220, prizePool: 50000 }]
  ]
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** The container's own `formatDayMeta`, reproduced so this fixture owns no import cycle. */
const demoDayMeta = (date: Date): DayMeta => ({
  dayKey: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`,
  dayName: DAY_NAMES[date.getDay()],
  dayDate: date.toLocaleDateString('en-US', { month: 'short', day: '2-digit' }),
  dayNum: String(date.getDate()).padStart(2, '0')
});

/**
 * Build production's schedule against `serverTime` (epoch seconds; 0 falls back
 * to this machine's clock). `bannerFor` is the container's own art picker,
 * passed in rather than imported so the fixture stays a leaf — the demo tiles
 * then wear exactly the art production would give them.
 */
export const projectTournaments = (
  serverTime: number,
  bannerFor: (
    type: Tournament['type'],
    title: string,
    guarantee: number,
    startAtSeconds: number
  ) => string
): { tournaments: Tournament[]; days: DayMeta[] } => {
  const now = serverTime > 0 ? new Date(serverTime * 1000) : new Date();
  const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dayAt = (offset: number) =>
    new Date(midnight.getFullYear(), midnight.getMonth(), midnight.getDate() + offset);

  const plan: [number, readonly MttSlot[]][] = [];
  for (let offset = 0; offset < MTT_CARD_DAYS; offset += 1) {
    plan.push([offset, PRODUCTION_MTT_WEEK[dayAt(offset).getDay()]]);
  }
  PRODUCTION_MTT_SPECIALS.forEach(([offset, slots]) => plan.push([offset, slots]));

  const tournaments: Tournament[] = [];
  plan.forEach(([offset, slots]) => {
    const day = demoDayMeta(dayAt(offset));
    slots.forEach((slot) => {
      const [hour, minute] = slot.at;
      const start = dayAt(offset);
      start.setHours(hour, minute, 0, 0);
      const startAt = Math.floor(start.getTime() / 1000);
      const guarantee = slot.prizePool;
      tournaments.push({
        id: `${DEMO_TABLE_PREFIX}mtt-${day.dayKey}-${String(hour).padStart(2, '0')}${String(
          minute
        ).padStart(2, '0')}`,
        title: slot.title,
        banner: bannerFor(slot.type, slot.title, guarantee, startAt),
        startTime: start.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' }),
        startAt,
        dayKey: day.dayKey,
        dayName: day.dayName,
        dayDate: day.dayDate,
        buyIn: slot.buyIn,
        prizePool: slot.prizePool,
        guarantee,
        satelliteSeats: slot.seats ?? 0,
        registered: slot.registered ?? 0,
        // Production does not show a cap on these tiles; it carries one anyway.
        capacity: 500,
        type: slot.type,
        typeLabel: slot.typeLabel,
        // Announced unless the recipe says registration is open.
        status: slot.status ?? 'A',
        isRegistered: false,
        isSatellite: slot.type === 'Satellite'
      });
    });
  });

  // One contiguous run of days, today through the last event, exactly as
  // adaptTournaments builds it. The empty ones never render (see
  // TournamentDayCarousel), which is what leaves production's gappy day row.
  const lastOffset = plan.reduce((max, [offset]) => Math.max(max, offset), 0);
  const days: DayMeta[] = [];
  for (let offset = 0; offset <= lastOffset; offset += 1) days.push(demoDayMeta(dayAt(offset)));

  return { tournaments, days };
};
